<template>
  <div class="cbm-card-flex">
    <cbm-card
      v-for="(card, index) in cards"
      :key="index"
      :number-text="card.value"
      :title="card.text"
      :tooltip="card.helpText"
      :boolean-loader="$apollo.queries.agregatedCampaignMetrics.loading"
      :percent-text="card.underText"
      :percent-description="card.description"
      :is-currency="card.isCurrency"
      unset-percentage-colors
      simple-card
    />
  </div>
</template>

<script>
import capitalize from 'lodash/capitalize'
import { mapState } from 'vuex'
import { localeString } from '@/config/helpers.js'
import { QUERY_GET_AGGREGATED_CAMPAIGN_METRICS } from '@/modules/calendario-promocional/graphql'
import { DateTime } from 'luxon'

export default {
  components: {
    CbmCard: () => import('@/components/CbmCard.vue'),
  },
  data: () => ({
    capitalize: capitalize,
  }),
  computed: {
    ...mapState({
      currentMonth: (state) => state.calendarioPromocional.currentMonth,
      selectedModes: (state) => state.calendarioPromocional.selectedModes,
      isMariaSelected: (state) => state.calendarioPromocional.isMariaSelected,
    }),
    campaignTypes() {
      if (this.selectedModes.length && !this.isMariaSelected) return ['SINGLE_CHANNEL']
      else if (!this.selectedModes.length && this.isMariaSelected) return ['OMNI_CHANNEL']
      else return []
    },
    cards() {
      return [
        {
          text: 'Clientes atingidos',
          value: localeString(this.agregatedCampaignMetrics?.clients_reached || 0),
          helpText: 'Clientes que receberam as mensagens das campanhas criadas, independente se foram lidas ou não.',
          underText: `${localeString(this.agregatedCampaignMetrics?.perc_clients_reached || 0, 1)}%`,
          description: 'da base',
        },
        {
          text: 'Receberam e compraram',
          value: localeString(this.agregatedCampaignMetrics?.clients_received_bought || 0),
          helpText: 'Quantidade de clientes que participaram de campanhas e realizaram pelo menos uma compra identificada no período de mensuração.',
          underText: `${localeString(this.agregatedCampaignMetrics?.perc_reached_bought || 0, 1)}%`,
          description: 'do total de clientes atingidos',
        },
        {
          text: 'Faturamento',
          value: localeString(this.agregatedCampaignMetrics?.campaign_revenue || 0, 0),
          isCurrency: true,
          helpText: 'Total de faturamento gerado pelos clientes que receberam e compraram.',
          underText: `${localeString(this.agregatedCampaignMetrics?.perc_revenue || 0, 1)}%`,
          description: 'do faturamento total',
        },
        {
          text: 'Total de campanhas',
          value: localeString(this.agregatedCampaignMetrics?.campaigns_qty || 0),
          underText: `${this.agregatedCampaignMetrics?.finished_campaigns_qty || 0}`,
          description: 'campanhas concluídas',
        },
      ]
    },
  },
  apollo: {
    agregatedCampaignMetrics: {
      query: QUERY_GET_AGGREGATED_CAMPAIGN_METRICS,
      fetchPolicy: 'network-only',
      skip() {
        return !Object.entries(this.currentMonth).length
      },
      variables() {
        if (!Object.entries(this.currentMonth).length) {
          return {}
        }
        const curYear = this.currentMonth.year
        const curMonth = this.currentMonth.month
        return {
          mode: this.selectedModes,
          status: ['ACTIVE', 'FINISHED'],
          measuringDate: [
            DateTime.fromObject({ year: curYear, month: curMonth, day: 1 }).toFormat('yyyy-MM-dd'),
            DateTime.fromObject({ year: curYear, month: curMonth, day: 1 }).plus({ months: 1, days: -1 }).toFormat('yyyy-MM-dd'),
          ],
          campaignTypes: this.campaignTypes,
        }
      },
      update({ aggregatedCampaignMetrics = {} }) {
        return {
          ...aggregatedCampaignMetrics,
          perc_clients_reached: aggregatedCampaignMetrics?.clients_total
            ? (aggregatedCampaignMetrics?.clients_reached / aggregatedCampaignMetrics?.clients_total) * 100
            : 0,
          perc_reached_bought: aggregatedCampaignMetrics?.clients_reached
            ? (aggregatedCampaignMetrics?.clients_received_bought / aggregatedCampaignMetrics?.clients_reached) * 100
            : 0,
          perc_revenue: aggregatedCampaignMetrics?.total_revenue
            ? (aggregatedCampaignMetrics?.campaign_revenue / aggregatedCampaignMetrics?.total_revenue) * 100
            : 0,
        }
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.cbm-card-flex {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
